import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from "lucide-react";
import type { ReactElement } from "react";
import { PtzButton } from "./PtzButton.tsx";
import type { PtzDir } from "../../lib/usePtzContinuousControl.ts";

const ICON_SIZE = 15;
const ICON_STROKE = 2.25;

type PanTiltDir = Extract<PtzDir, "up" | "down" | "left" | "right">;

export type PtzEdgeDirectionPadProps = {
  counts: Record<PanTiltDir, number>;
  isFull: (dir: PanTiltDir) => boolean;
  atLimit: Record<PanTiltDir, boolean>;
  operable: boolean;
  onPressStart: (dir: PanTiltDir) => void;
  onPressEnd: () => void;
};

function EdgeDirButton({
  dir,
  ariaLabel,
  slotClass,
  icon,
  counts,
  isFull,
  atLimit,
  operable,
  onPressStart,
  onPressEnd,
}: {
  dir: PanTiltDir;
  ariaLabel: string;
  slotClass: string;
  icon: ReactElement;
  counts: Record<PanTiltDir, number>;
  isFull: (dir: PanTiltDir) => boolean;
  atLimit: Record<PanTiltDir, boolean>;
  operable: boolean;
  onPressStart: (dir: PanTiltDir) => void;
  onPressEnd: () => void;
}): ReactElement {
  const count = counts[dir];
  return (
    <div className={slotClass}>
      <PtzButton
        className="ds-ptz-edge-pad__btn"
        ariaLabel={ariaLabel}
        count={count}
        full={isFull(dir)}
        disabled={atLimit[dir] || !operable}
        ariaSuffix={count > 0 ? "moving" : undefined}
        onPressStart={() => onPressStart(dir)}
        onPressEnd={onPressEnd}
      >
        {icon}
      </PtzButton>
    </div>
  );
}

/** Four separate edge arrows (top / right / bottom / left) for PTZ full-screen only. */
export function PtzEdgeDirectionPad({
  counts,
  isFull,
  atLimit,
  operable,
  onPressStart,
  onPressEnd,
}: PtzEdgeDirectionPadProps): ReactElement {
  return (
    <div
      className="ds-ptz-edge-pad"
      role="group"
      aria-label="PTZ direction controls"
    >
      <EdgeDirButton
        dir="up"
        ariaLabel="Tilt up"
        slotClass="ds-ptz-edge-pad__slot ds-ptz-edge-pad__slot--top"
        icon={
          <ChevronUp size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
        }
        counts={counts}
        isFull={isFull}
        atLimit={atLimit}
        operable={operable}
        onPressStart={onPressStart}
        onPressEnd={onPressEnd}
      />
      <EdgeDirButton
        dir="right"
        ariaLabel="Pan right"
        slotClass="ds-ptz-edge-pad__slot ds-ptz-edge-pad__slot--right"
        icon={
          <ChevronRight size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
        }
        counts={counts}
        isFull={isFull}
        atLimit={atLimit}
        operable={operable}
        onPressStart={onPressStart}
        onPressEnd={onPressEnd}
      />
      <EdgeDirButton
        dir="left"
        ariaLabel="Pan left"
        slotClass="ds-ptz-edge-pad__slot ds-ptz-edge-pad__slot--left"
        icon={
          <ChevronLeft size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
        }
        counts={counts}
        isFull={isFull}
        atLimit={atLimit}
        operable={operable}
        onPressStart={onPressStart}
        onPressEnd={onPressEnd}
      />
      <EdgeDirButton
        dir="down"
        ariaLabel="Tilt down"
        slotClass="ds-ptz-edge-pad__slot ds-ptz-edge-pad__slot--bottom"
        icon={
          <ChevronDown size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
        }
        counts={counts}
        isFull={isFull}
        atLimit={atLimit}
        operable={operable}
        onPressStart={onPressStart}
        onPressEnd={onPressEnd}
      />
    </div>
  );
}
